import { useEffect, useRef, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import useViewport from '@cartwhl/ui/hooks/useViewport';

import SectionHeader from 'components/SectionHeader';
import { VideoPlayer, type VideoPlayerRef } from 'components/VideoPlayer';
import { CDN } from 'consts';

import VideoProgressButton from './VideoProgressButton';

const heroTabs = [
  { label: 'for games', videoUrl: `${CDN}Games_Final_outputV3.webm` },
  {
    label: 'for ads & social',
    videoUrl: `${CDN}Ads&Social_Final_outputV3.webm`,
  },
  {
    label: 'for film & tv',
    videoUrl: `${CDN}Film&TV_Final_outputV3.webm`,
  },
  {
    label: 'for the real world',
    videoUrl: `${CDN}Real_World_outputV3.webm`,
  },
];

export default function HeroSection() {
  const [activeTab, setActiveTab] = useState(() =>
    Math.floor(Math.random() * heroTabs.length),
  );
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [canScrollMobileTabsPrev, setCanScrollMobileTabsPrev] = useState(false);
  const [canScrollMobileTabsNext, setCanScrollMobileTabsNext] = useState(false);
  const videoPlayerRef = useRef<VideoPlayerRef | null>(null);
  const mobileTabsScrollRef = useRef<HTMLDivElement | null>(null);

  const { height: viewportHeight, isLg } = useViewport();

  const handleTabChange = (index: number) => {
    if (activeTab === index) return;

    setActiveTab(index);
    setIsPlaying(true);
    setProgress(0);
  };

  useEffect(() => {
    const scrollElement = mobileTabsScrollRef.current;

    if (!scrollElement) return;

    const updateCanScrollMobileTabs = () => {
      setCanScrollMobileTabsPrev(scrollElement.scrollLeft > 4);
      setCanScrollMobileTabsNext(
        scrollElement.scrollLeft + scrollElement.clientWidth <
          scrollElement.scrollWidth - 4,
      );
    };

    updateCanScrollMobileTabs();

    scrollElement.addEventListener('scroll', updateCanScrollMobileTabs, {
      passive: true,
    });
    window.addEventListener('resize', updateCanScrollMobileTabs);

    return () => {
      scrollElement.removeEventListener('scroll', updateCanScrollMobileTabs);
      window.removeEventListener('resize', updateCanScrollMobileTabs);
    };
  }, []);

  const handleMobileTabsScroll = (direction: 'prev' | 'next') => {
    mobileTabsScrollRef.current?.scrollBy({
      left:
        mobileTabsScrollRef.current.clientWidth *
        0.75 *
        (direction === 'next' ? 1 : -1),
      behavior: 'smooth',
    });
  };

  return (
    <section className="pt-[calc(var(--vertical-spacing)-16px)]">
      <div className="container">
        <SectionHeader
          title="Accelerate the art and science of motion"
          bottomMarginClassName="mb-8"
        />
      </div>
      <div className="px-0 md:px-8">
        <div className="group relative mx-auto max-w-[1984px]">
          <VideoPlayer
            playerRef={videoPlayerRef}
            videoUrl={heroTabs[activeTab].videoUrl}
            videoKey={activeTab}
            toggleOnClick={false}
            showHoverGradient={false}
            onVideoEnded={() => {
              setActiveTab(
                (previousTab) => (previousTab + 1) % heroTabs.length,
              );
              setIsPlaying(true);
              setProgress(0);
            }}
            onProgressChange={setProgress}
            onPlayPauseChange={setIsPlaying}
            className="rounded-none md:rounded-2xl lg:rounded-3xl"
          >
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 20%, rgba(22, 22, 22, 0) 0%, rgba(22, 22, 22, 0.2) 58%, rgba(22, 22, 22, 0.35) 100%), linear-gradient(180deg, rgba(22, 22, 22, 0) 52%, rgba(22, 22, 22, 0.24) 100%)',
              }}
            />
            <div
              className="aspect-394/208 lg:aspect-video lg:max-h-[1160px] lg:min-h-[500px]"
              style={{
                maxHeight: isLg ? `${viewportHeight - 128 - 32}px` : undefined,
              }}
            />

            {/* Controls overlay - always on video */}
            <div
              className="absolute inset-x-0 bottom-4 hidden justify-center lg:bottom-9 lg:flex"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="container flex items-center gap-4 lg:gap-8">
                <VideoProgressButton
                  progress={progress}
                  isPlaying={isPlaying}
                  onToggle={() => {
                    videoPlayerRef.current?.togglePlayPause();
                  }}
                />
                <div
                  className="flex items-center gap-4 lg:gap-8"
                  role="tablist"
                >
                  {heroTabs.map((tab, index) => (
                    <button
                      key={tab.label}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === index}
                      onClick={() => handleTabChange(index)}
                      className="hidden cursor-pointer transition-opacity hover:opacity-80 lg:block"
                    >
                      <Text
                        variant="web/body/medium/24"
                        className={
                          activeTab === index
                            ? 'text-green-50'
                            : 'text-white hover:text-white'
                        }
                      >
                        {tab.label}
                      </Text>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="absolute bottom-4 right-4 lg:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-9 w-14 items-center justify-center rounded-[10px] bg-white shadow-[0_6px_18px_rgba(0,0,0,0.16)]">
                <VideoProgressButton
                  progress={progress}
                  isPlaying={isPlaying}
                  onToggle={() => {
                    videoPlayerRef.current?.togglePlayPause();
                  }}
                  className="text-gray-90 hover:text-gray-60"
                  trackClassName="stroke-gray-45"
                  size={24}
                  strokeWidth={2.8}
                />
              </div>
            </div>
          </VideoPlayer>

          {/* Hero Tabs - mobile only (outside video) */}
          <div
            className="cw-text-box-none container relative py-3 lg:hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left gradient fade */}
            <div className="dark:from-gray-90/40 bg-linear-to-r pointer-events-none absolute left-0 top-0 z-10 h-full w-8 from-white to-transparent" />
            {/* Right linear fade */}
            <div className="dark:from-gray-90/40 bg-linear-to-l pointer-events-none absolute right-0 top-0 z-10 h-full w-8 from-white to-transparent" />
            <div
              ref={mobileTabsScrollRef}
              className="no-scrollbar overflow-x-auto"
            >
              <div
                className="flex items-center gap-4 pl-0 pr-20"
                role="tablist"
              >
                {heroTabs.map((tab, index) => (
                  <button
                    key={tab.label}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === index}
                    onClick={() => handleTabChange(index)}
                    className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
                  >
                    <Text
                      variant="web/body/medium/md"
                      className={
                        activeTab === index
                          ? 'text-green-50'
                          : 'text-gray-60 hover:text-gray-90'
                      }
                    >
                      {tab.label}
                    </Text>
                  </button>
                ))}
              </div>
            </div>
            {canScrollMobileTabsPrev && (
              <button
                type="button"
                onClick={() => handleMobileTabsScroll('prev')}
                aria-label="Scroll hero tabs left"
                className="text-gray-90 absolute left-2 top-1/2 z-20 flex h-8 w-6 -translate-y-1/2 items-center justify-center bg-white"
              >
                <ArrowRightIcon className="h-4 w-4 rotate-180" />
                <span className="dark:from-gray-90/40 bg-linear-to-l pointer-events-none absolute left-full z-10 h-full w-5 from-transparent to-white" />
              </button>
            )}
            {canScrollMobileTabsNext && (
              <button
                type="button"
                onClick={() => handleMobileTabsScroll('next')}
                aria-label="Scroll hero tabs right"
                className="text-gray-90 absolute right-2 top-1/2 z-20 flex h-8 w-6 -translate-y-1/2 items-center justify-center bg-white"
              >
                <ArrowRightIcon className="h-4 w-4" />
                <span className="dark:from-gray-90/40 bg-linear-to-r pointer-events-none absolute right-full z-10 h-full w-5 from-transparent to-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const ArrowRightIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      viewBox="0 0 16 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9.26318 12.8569C9.00863 12.8569 8.79557 12.7739 8.62402 12.6079C8.45801 12.4419 8.375 12.2344 8.375 11.9854C8.375 11.8581 8.39714 11.7391 8.44141 11.6284C8.49121 11.5177 8.55762 11.4209 8.64062 11.3379L10.5 9.46191L13.8037 6.43213L10.5 3.40234L8.64062 1.51807C8.55762 1.43506 8.49121 1.34098 8.44141 1.23584C8.39714 1.12516 8.375 1.00342 8.375 0.870605C8.375 0.621582 8.45801 0.414062 8.62402 0.248047C8.79557 0.0820312 9.00863 -0.000976562 9.26318 -0.000976562C9.38493 -0.000976562 9.50114 0.0239258 9.61182 0.0737305C9.72249 0.123535 9.82764 0.198242 9.92725 0.297852L15.3975 5.75146C15.5911 5.94515 15.688 6.17204 15.688 6.43213C15.688 6.69222 15.5911 6.91911 15.3975 7.11279L9.91895 12.5747C9.72526 12.7629 9.50667 12.8569 9.26318 12.8569ZM0.920898 7.33691C0.644206 7.33691 0.420085 7.25391 0.248535 7.08789C0.0825195 6.91634 -0.000488281 6.69775 -0.000488281 6.43213C-0.000488281 6.16097 0.0825195 5.94238 0.248535 5.77637C0.420085 5.60482 0.644206 5.51904 0.920898 5.51904H11.0063L13.8867 5.69336C14.1136 5.70443 14.2962 5.77637 14.4346 5.90918C14.5729 6.03646 14.6421 6.21077 14.6421 6.43213C14.6421 6.64795 14.5729 6.82227 14.4346 6.95508C14.2962 7.08236 14.1136 7.1543 13.8867 7.1709L11.0063 7.33691H0.920898Z"
        fill="currentColor"
      />
    </svg>
  );
};
